import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export interface ProjectFeature {
  category: string;
  icon: string;
  items: string[];
}

const CATEGORIES: { title: string; icon: string; items: string[] }[] = [
  { title: 'Main Features', icon: 'Zap', items: ['Central Air Conditioning', 'Central Heating', 'Double Glazed Windows', 'Waste Disposal', 'Electricity Backup / Generator', 'Furnished', 'Semi-Furnished', 'Marble / Tile Flooring', 'Wooden Flooring', 'Built-in Wardrobes', 'Solar Panels', 'Water Tank / Cistern'] },
  { title: 'Rooms', icon: 'Building', items: ['Drawing Room', 'Dining Room', 'Kitchen', 'Study Room / Office', 'Prayer Room', 'Gymnasium / Gym', 'Store Room', 'Laundry Room', 'Servant Quarter', 'Guest Room', 'Balcony'] },
  { title: 'Business & Communication', icon: 'Wifi', items: ['Broadband Internet', 'Satellite / Cable TV', 'Intercom', 'Conference Room', 'CCTV Security', 'Fire Alarm System'] },
  { title: 'Healthcare & Recreational', icon: 'Zap', items: ['Lawn / Garden', 'Swimming Pool', 'Sauna / Steam Room', 'Jacuzzi', 'Kids Play Area', 'Barbecue Area', 'Community Hall', 'Rooftop Terrace'] },
  { title: 'Nearby Locations & Facilities', icon: 'MapPin', items: ['Near Schools', 'Near Hospitals', 'Near Shopping Malls', 'Near Restaurants', 'Near Airport', 'Near Public Transport', 'Near Parks', 'Near Places of Worship'] },
  { title: 'Other Facilities', icon: 'Shield', items: ['Maintenance Staff', 'Security Staff / Guard', 'Facilities for Disabled', 'Elevator / Lift', 'Parking (Covered)', 'Parking (Uncovered)', 'Pet Friendly', 'Gated Community'] },
];

interface Props {
  features: ProjectFeature[];
  setFeatures: (updater: (prev: ProjectFeature[]) => ProjectFeature[]) => void;
}

export function ProjectAmenitiesPicker({ features, setFeatures }: Props) {
  const getItems = (cat: string) => features.find(f => f.category === cat)?.items || [];

  const toggle = (cat: string, icon: string, item: string) => {
    setFeatures(prev => {
      const existing = prev.find(f => f.category === cat);
      if (!existing) {
        return [...prev.filter(f => f.category.trim()), { category: cat, icon, items: [item] }];
      }
      const has = existing.items.includes(item);
      const nextItems = has ? existing.items.filter(x => x !== item) : [...existing.items, item];
      return prev.map(f => f.category === cat ? { ...f, items: nextItems, icon: f.icon || icon } : f);
    });
  };

  const total = features.reduce((s, f) => s + (f.items?.length || 0), 0);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Select amenities buyers should know about. <strong className="text-foreground">{total}</strong> selected.
      </p>
      <Accordion type="multiple" defaultValue={['Main Features']} className="w-full">
        {CATEGORIES.map(cat => {
          const selected = getItems(cat.title);
          return (
            <AccordionItem key={cat.title} value={cat.title}>
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2">
                  <span>{cat.title}</span>
                  {selected.length > 0 && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">{selected.length}</span>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                  {cat.items.map(item => {
                    const checked = selected.includes(item);
                    return (
                      <label key={item} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/50 rounded px-2 py-1.5">
                        <Checkbox checked={checked} onCheckedChange={() => toggle(cat.title, cat.icon, item)} />
                        <span>{item}</span>
                      </label>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
